import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import type { OptionItem } from '@wartek-id/select'
import { CustomSelect } from '@wartek-id/select'
import { Shimmer } from '@wartek-id/shimmer'

import {
  useAPIKinerja,
  useAPIContentPerilakuKerja,
} from 'api/kinerja/useAPIKinerja'
import { PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { useUpsertPerencanaanPegawai } from 'app/KinerjaShared/hooks/useUpsertPerencanaanPegawai'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { UpsertSKPPayload } from 'types/kinerja'

type PerilakuKerjaProps = {
  onNext: () => void
}

export function PerilakuKerja({ onNext }: PerilakuKerjaProps) {
  const router = useRouter()
  const { periodYear, periodNo } = usePeriodContext()

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000, // 1 hour,
      },
      {
        tipe: 'SPNF',
      }
    )

  const currentPerilakuKerja = perencanaanData?.data?.prilakuKerja

  // -----------------------------------------
  // Get Perilaku Kerja content
  // -----------------------------------------
  const {
    data: dataContentPerilakuKerja,
    isLoading: isLoadingContentPerilakuKerja,
  } = useAPIContentPerilakuKerja(
    {
      tipe: 'SPNF',
      periodParams: {
        skp_year: periodYear,
        skp_period: periodNo,
      },
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )

  const aspekList = useMemo(
    () => dataContentPerilakuKerja?.data?.aspek ?? [],
    [dataContentPerilakuKerja]
  )

  // -----------------------------------------
  // Handle form data
  // -----------------------------------------
  const [selectionsPerAspek, setSelectionsPerAspek] = useState<
    Record<number, { indikatorId?: number; ekspektasiId?: number }>
  >({})

  // Sync with perencanaan data
  useEffect(() => {
    if (currentPerilakuKerja && aspekList.length > 0) {
      const initialSelectionsPerAspek = {}
      currentPerilakuKerja.forEach((perilakuKerja) => {
        initialSelectionsPerAspek[perilakuKerja.aspekId] = {
          indikatorId: perilakuKerja.indikatorId,
          ekspektasiId: perilakuKerja.ekspektasiId,
        }
      })

      setSelectionsPerAspek(initialSelectionsPerAspek)
    }
  }, [currentPerilakuKerja, aspekList])

  const handleIndikatorChange = (
    aspekId: number,
    selectedIndikator: OptionItem
  ) => {
    setSelectionsPerAspek((prev) => ({
      ...prev,
      [aspekId]: {
        indikatorId: selectedIndikator.value as number,
        ekspektasiId: undefined, // Reset ekspektasi when indikator changes
      },
    }))
  }

  const handleEkspektasiChange = (
    aspekId: number,
    selectedEkspektasi: OptionItem
  ) => {
    setSelectionsPerAspek((prev) => ({
      ...prev,
      [aspekId]: {
        ...prev[aspekId],
        ekspektasiId: selectedEkspektasi.value as number,
      },
    }))
  }

  // Check if any selection has been made
  const isDirty = useMemo(() => {
    const numOfSelectedAspek = Object.keys(selectionsPerAspek).length
    // If no selection has been made, return false
    if (numOfSelectedAspek === 0) return false

    // If the number of aspek from perencanaan datais different from the current selections, return true
    if (currentPerilakuKerja?.length !== numOfSelectedAspek) return true

    // Check if any selection is different from the perencanaan data
    const isChanged = currentPerilakuKerja?.some((item) => {
      const selection = selectionsPerAspek[item.aspekId]

      return (
        selection?.indikatorId !== item.indikatorId ||
        selection?.ekspektasiId !== item.ekspektasiId
      )
    })

    return isChanged
  }, [selectionsPerAspek, currentPerilakuKerja])

  const isValidated = useMemo(() => {
    // If no selection has been made, return false
    if (Object.keys(selectionsPerAspek).length === 0) return false

    // Check if any aspek is unselected
    const hasUnselectedAspek = aspekList.some(
      (aspekItem) =>
        !selectionsPerAspek[aspekItem.id] ||
        !selectionsPerAspek[aspekItem.id].indikatorId ||
        !selectionsPerAspek[aspekItem.id].ekspektasiId
    )

    return !hasUnselectedAspek
  }, [aspekList, selectionsPerAspek])

  // -----------------------------------------
  // Handle actions
  // -----------------------------------------
  const { handleUpsertMutation, isLoading: isLoadingUpsertPerencanaan } =
    useUpsertPerencanaanPegawai()

  const payload: UpsertSKPPayload = useMemo(
    () => ({
      id: perencanaanData?.data?.id,
      prilakuKerja: Object.entries(selectionsPerAspek).map(
        ([aspekId, selection]) => ({
          aspekId: Number(aspekId),
          indikatorId: selection.indikatorId,
          ekspektasiId: selection.ekspektasiId,
        })
      ),
      tipe: 'SPNF',
    }),
    [perencanaanData, selectionsPerAspek]
  )

  const handleSaveToDraft = () =>
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !isDirty,
      showSuccessMessage: true,
      onSuccess: () => router.push(PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH),
    })
  const handleNext = () =>
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !isDirty,
      onSuccess: onNext,
    })

  // -----------------------------------------
  // Other derivatives
  // -----------------------------------------
  const isLoadingQueries = isPerencanaanLoading || isLoadingContentPerilakuKerja

  return (
    <>
      <div className="p-9">
        <Text variant="heading-md" className="mb-4">
          Pilih 1 Indikator Perilaku dan 1 Ekspektasi Khusus Pimpinan
        </Text>
        <Text className="text-justify mb-4">
          Untuk Ekspektasi Khusus Pimpinan, pilih yang Anda ingin usulkan ke
          Kepala SKB.
        </Text>
        <a
          href={`${PENGELOLAAN_KINERJA_PAMONG_PEGAWAI_PATH}/panduan/perilaku-kerja`}
          className="underline text-blue-70 font-bold"
          target="_blank"
          rel="noopener noreferrer"
        >
          Cek Daftar Perilaku Kerja
        </a>

        {isLoadingQueries ? (
          <div className="mt-10 space-y-6">
            <Shimmer height="200px" width="100%" />
            <Shimmer height="200px" width="100%" />
            <Shimmer height="200px" width="100%" />
            <Shimmer height="200px" width="100%" />
            <Shimmer height="200px" width="100%" />
            <Shimmer height="200px" width="100%" />
          </div>
        ) : (
          <div className="mt-10 flex flex-col gap-6">
            {aspekList?.map((aspek, aspekIndex) => {
              const selection = selectionsPerAspek[aspek.id]
              const selectedIndikatorId = selection?.indikatorId
              const selectedEkspektasiId = selection?.ekspektasiId

              const indikatorOptions: OptionItem[] = aspek.indikator.map(
                (indikator) => ({
                  value: indikator.id,
                  name: indikator.value,
                })
              )

              const selectedIndikatorOption = selectedIndikatorId
                ? indikatorOptions.find(
                    (opt) => opt.value === selectedIndikatorId
                  )
                : undefined

              const ekspektasiOptions: OptionItem[] = selectedIndikatorId
                ? aspek.indikator
                    .find((indikator) => indikator.id === selectedIndikatorId)
                    ?.ekspektasi.map((ekspektasi) => ({
                      value: ekspektasi.id,
                      name: ekspektasi.value,
                    })) ?? []
                : []

              const selectedEkspektasiOption = selectedEkspektasiId
                ? ekspektasiOptions.find(
                    (opt) => opt.value === selectedEkspektasiId
                  )
                : undefined

              return (
                <div
                  key={`perilaku-kerja-${aspek.id}`}
                  className="p-6 border border-subdued rounded"
                >
                  <div className="flex items-start gap-2.5">
                    <div className="flex items-center justify-center rounded-full bg-blue-70 w-6 h-6 flex-shrink-0">
                      <Text color="inverse" variant="body-sm-bold" as="span">
                        {aspekIndex + 1}
                      </Text>
                    </div>
                    <div className="space-y-1">
                      <Text variant="body-sm" color="subdued">
                        Aspek
                      </Text>
                      <Text variant="heading-lg">{aspek.value}</Text>
                    </div>
                  </div>

                  <div className="mt-6">
                    <Text
                      variant="body-sm"
                      color="subdued"
                      className="mb-1"
                      id={`indikator-perilaku-${aspek.id}`}
                    >
                      Indikator Perilaku
                    </Text>
                    <CustomSelect
                      value={selectedIndikatorOption}
                      placeholder="Pilih"
                      options={indikatorOptions}
                      aria-labelledby={`indikator-perilaku-${aspek.id}`}
                      onChange={(value) =>
                        handleIndikatorChange(aspek.id, value as OptionItem)
                      }
                    />
                  </div>

                  <div className="mt-4">
                    <Text
                      variant="body-sm"
                      color="subdued"
                      className="mb-1"
                      id={`ekspektasi-khusus-pimpinan-${aspek.id}`}
                    >
                      Ekspektasi Khusus Pimpinan
                    </Text>
                    <CustomSelect
                      value={selectedEkspektasiOption}
                      placeholder="Pilih"
                      options={ekspektasiOptions}
                      isDisabled={!selectedIndikatorId}
                      aria-labelledby={`ekspektasi-khusus-pimpinan-${aspek.id}`}
                      onChange={(value) =>
                        handleEkspektasiChange(aspek.id, value as OptionItem)
                      }
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
      <div className="border-t border-subdued px-6 py-4 flex justify-end gap-5">
        <Button
          size="lg"
          color="white"
          onClick={handleSaveToDraft}
          disabled={isPerencanaanLoading || !isDirty}
        >
          Simpan draf
        </Button>

        <Button
          color="black"
          size="lg"
          disabled={
            isPerencanaanLoading || isLoadingUpsertPerencanaan || !isValidated
          }
          onClick={handleNext}
        >
          Ke Rangkuman
        </Button>
      </div>
    </>
  )
}
